import { z } from 'zod'

import { GUEST_MEDIA_MIME_TYPES } from '~publish/helpers/guestMedia'

const STORAGE_KEY = 'buffer:publish:guest-handoff-journal'
const STORAGE_VERSION = 1

const PermanentUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    const url = new URL(value)
    return url.protocol === 'https:' && url.search === '' && url.hash === ''
  })

const PromotedGuestMediaSchema = z
  .object({
    guestMediaId: z.string().min(1),
    sourceUploadedAt: z.number().int().nonnegative(),
    url: PermanentUrlSchema,
    thumbnailUrl: PermanentUrlSchema,
    mimeType: z.enum(GUEST_MEDIA_MIME_TYPES),
    altText: z.string().optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
  })
  .strict()

const GuestDraftJournalEntrySchema = z
  .object({
    draftId: z.string().min(1),
    media: z.array(PromotedGuestMediaSchema).max(10),
    contentItemId: z.string().min(1).optional(),
    sourceUpdatedAt: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine(
    (entry) =>
      new Set(entry.media.map((media) => media.guestMediaId)).size ===
      entry.media.length,
  )

const GuestDraftHandoffJournalSchema = z
  .object({
    version: z.literal(STORAGE_VERSION),
    intentId: z.string().uuid(),
    organizationId: z.string().min(1).optional(),
    attempts: z.number().int().nonnegative().default(0),
    drafts: z.array(GuestDraftJournalEntrySchema).max(10),
  })
  .strict()
  .refine(
    (journal) =>
      new Set(journal.drafts.map((draft) => draft.draftId)).size ===
      journal.drafts.length,
  )

export type PromotedGuestMedia = Readonly<
  z.infer<typeof PromotedGuestMediaSchema>
>

export type GuestDraftJournalEntry = Readonly<
  z.infer<typeof GuestDraftJournalEntrySchema>
>

export type GuestDraftHandoffJournal = Readonly<
  z.infer<typeof GuestDraftHandoffJournalSchema>
>

function createJournal(intentId: string): GuestDraftHandoffJournal {
  return { version: STORAGE_VERSION, intentId, attempts: 0, drafts: [] }
}

export function readGuestDraftHandoffJournal(
  intentId: string,
): GuestDraftHandoffJournal {
  let serializedJournal: string | null
  try {
    serializedJournal =
      window.localStorage.getItem(`${STORAGE_KEY}:${intentId}`) ??
      window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return createJournal(intentId)
  }

  if (serializedJournal === null) return createJournal(intentId)

  let parsedJournal: unknown
  try {
    parsedJournal = JSON.parse(serializedJournal)
  } catch {
    return createJournal(intentId)
  }

  const result = GuestDraftHandoffJournalSchema.safeParse(parsedJournal)
  return result.success && result.data.intentId === intentId
    ? result.data
    : createJournal(intentId)
}

export function hasGuestDraftHandoffJournal(intentId: string): boolean {
  try {
    const serializedJournal =
      window.localStorage.getItem(`${STORAGE_KEY}:${intentId}`) ??
      window.localStorage.getItem(STORAGE_KEY)
    if (serializedJournal === null) return false

    const result = GuestDraftHandoffJournalSchema.safeParse(
      JSON.parse(serializedJournal),
    )
    return result.success && result.data.intentId === intentId
  } catch {
    return false
  }
}

export function findJournalDraft(
  journal: GuestDraftHandoffJournal,
  draftId: string,
): GuestDraftJournalEntry | undefined {
  return journal.drafts.find((draft) => draft.draftId === draftId)
}

export function setJournalMedia(
  journal: GuestDraftHandoffJournal,
  draftId: string,
  media: PromotedGuestMedia,
): GuestDraftHandoffJournal {
  const existingDraft = findJournalDraft(journal, draftId)
  const nextDraft = existingDraft
    ? {
        ...existingDraft,
        media: existingDraft.media.some(
          (item) => item.guestMediaId === media.guestMediaId,
        )
          ? existingDraft.media.map((item) =>
              item.guestMediaId === media.guestMediaId ? media : item,
            )
          : [...existingDraft.media, media],
      }
    : { draftId, media: [media] }

  return {
    ...journal,
    drafts: existingDraft
      ? journal.drafts.map((draft) =>
          draft.draftId === draftId ? nextDraft : draft,
        )
      : [...journal.drafts, nextDraft],
  }
}

export function retainCurrentJournalMedia(
  journal: GuestDraftHandoffJournal,
  draftId: string,
  media: readonly { readonly id: string; readonly uploadedAt: number }[],
): GuestDraftHandoffJournal {
  const existingDraft = findJournalDraft(journal, draftId)
  if (!existingDraft) return journal

  const currentMedia = new Map(
    media.map((item) => [item.id, item.uploadedAt] as const),
  )
  const retainedMedia = existingDraft.media.filter(
    (item) => currentMedia.get(item.guestMediaId) === item.sourceUploadedAt,
  )
  if (retainedMedia.length === existingDraft.media.length) return journal

  const nextDraft = { ...existingDraft, media: retainedMedia }
  return {
    ...journal,
    drafts: journal.drafts.map((draft) =>
      draft.draftId === draftId ? nextDraft : draft,
    ),
  }
}

export function setJournalContentItemId(
  journal: GuestDraftHandoffJournal,
  draftId: string,
  contentItemId: string,
  sourceUpdatedAt?: number,
): GuestDraftHandoffJournal {
  const existingDraft = findJournalDraft(journal, draftId)
  const nextDraft = existingDraft
    ? { ...existingDraft, contentItemId, sourceUpdatedAt }
    : { draftId, media: [], contentItemId, sourceUpdatedAt }

  return {
    ...journal,
    drafts: existingDraft
      ? journal.drafts.map((draft) =>
          draft.draftId === draftId ? nextDraft : draft,
        )
      : [...journal.drafts, nextDraft],
  }
}

export function writeGuestDraftHandoffJournal(
  journal: GuestDraftHandoffJournal,
): void {
  const result = GuestDraftHandoffJournalSchema.safeParse(journal)
  if (!result.success) throw new Error('The draft import progress is invalid')

  try {
    window.localStorage.setItem(
      `${STORAGE_KEY}:${journal.intentId}`,
      JSON.stringify(result.data),
    )
  } catch {
    throw new Error('The draft import progress could not be saved')
  }
}

export function clearGuestDraftHandoffJournal(intentId: string): void {
  try {
    window.localStorage.removeItem(`${STORAGE_KEY}:${intentId}`)
    const serializedJournal = window.localStorage.getItem(STORAGE_KEY)
    if (serializedJournal === null) return

    const result = GuestDraftHandoffJournalSchema.safeParse(
      JSON.parse(serializedJournal),
    )
    if (!result.success || result.data.intentId !== intentId) return

    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    return
  }
}
